import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { SlidersHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RiskConfigDialog({ tenantId, tenantName }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async (o) => {
    setOpen(o);
    if (o) {
      try { const { data } = await api.get(`/platform/tenants/${tenantId}/risk-config`); setCfg(data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/platform/tenants/${tenantId}/risk-config`, {
        weights: cfg.weights, band_cutoffs: cfg.band_cutoffs,
        multipliers: cfg.multipliers, block_high_band: cfg.block_high_band,
      });
      toast.success("Risk config saved"); setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={load}>
      <DialogTrigger asChild>
        <Button data-testid={`risk-config-btn-${tenantId}`} variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Risk config
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Risk policy — {tenantName}</DialogTitle>
          <DialogDescription>Per-tenant weights, band cut-offs and deposit multipliers. No redeploy.</DialogDescription>
        </DialogHeader>
        {!cfg ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-sm font-medium">Dimension weights</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(cfg.weights).map((dim) => (
                  <label key={dim} className="flex items-center justify-between gap-2 text-sm">
                    <span className="capitalize text-muted-foreground">{dim.replace(/_/g, " ")}</span>
                    <Input data-testid={`risk-weight-${dim}`} type="number" step="0.1" className="h-8 w-20"
                      value={cfg.weights[dim]}
                      onChange={(e) => setCfg({ ...cfg, weights: { ...cfg.weights, [dim]: Number(e.target.value) } })} />
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-2 text-sm font-medium">Band cut-offs</p>
                <label className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Low max</span>
                  <Input data-testid="risk-cut-low" type="number" className="h-8 w-20" value={cfg.band_cutoffs.low_max}
                    onChange={(e) => setCfg({ ...cfg, band_cutoffs: { ...cfg.band_cutoffs, low_max: Number(e.target.value) } })} />
                </label>
                <label className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Medium max</span>
                  <Input data-testid="risk-cut-medium" type="number" className="h-8 w-20" value={cfg.band_cutoffs.medium_max}
                    onChange={(e) => setCfg({ ...cfg, band_cutoffs: { ...cfg.band_cutoffs, medium_max: Number(e.target.value) } })} />
                </label>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Deposit multipliers</p>
                {["low", "medium", "high"].map((b) => (
                  <label key={b} className="mt-2 flex items-center justify-between text-sm first:mt-0">
                    <span className="capitalize text-muted-foreground">{b}</span>
                    <Input data-testid={`risk-mult-${b}`} type="number" step="0.1" className="h-8 w-20" value={cfg.multipliers[b]}
                      onChange={(e) => setCfg({ ...cfg, multipliers: { ...cfg.multipliers, [b]: Number(e.target.value) } })} />
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              Block High-band riders (refuse rather than 2.0×)
              <Switch data-testid="risk-block-high" checked={!!cfg.block_high_band}
                onCheckedChange={(v) => setCfg({ ...cfg, block_high_band: v })} />
            </label>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="risk-config-save" onClick={save} disabled={saving || !cfg} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save risk policy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
